import { useEffect } from "react";
import { useLocation } from "react-router";

/**
 * On every new page, scroll to the top, or to the section named by the
 * address's #fragment (e.g. /how-it-works redirects to /#how-it-works).
 *
 * Mount once, above <Routes> (SecurSpaceContent). Its effect runs after the
 * page's own, so it has the last word on where a new page starts.
 *
 * Depends on `pathname`, not the whole `location`: report filters write every
 * keystroke into the query string, so a `location` dependency scrolled the page
 * to the top on each character typed.
 */
const useScrollOnPageChange = (): void => {
    const { pathname, hash } = useLocation();

    useEffect(() => {
        // The SPA renders after the browser's own jump to the fragment, so do it
        // here. Instant, not the site's smooth scroll: content above loads a moment
        // later and pushes the section down, and browser scroll anchoring only
        // holds the section in place once we're already there.
        // ponytail: Safari lacks scroll anchoring, so it can land ~120px short; re-scroll on resize if that matters.
        const section = hash ? document.getElementById(hash.slice(1)) : null;
        if (section) {
            section.scrollIntoView({ behavior: "instant" });
        } else {
            window.scrollTo(0, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- a hash or query change is not a new page
    }, [pathname]);
};

export default useScrollOnPageChange;
